import React from "react";
import { VIDEO_AUGMENTATION_SKILL_FOLDER_URL } from "constants/pipelines";
import type { LaunchableContentTabProps } from "components/Launchables/DeployRedesign/shared/content/tabs/LaunchableContentTab";
import {
  InlineLink,
  Paragraph,
  PromptExample,
  SectionHeading,
} from "../shared";
import { PhysicalAISkin } from "../PhysicalAISkin";
const VIDEO_AUGMENTATION_HEADER = {
  title: "Video Data Augmentation",
  summary:
    "Turn real or simulated RGB video into diverse, model-ready physical AI datasets using agent skills to curate, augment, auto-label, and generate training data in one unified workflow.",
  badges: ["NVIDIA Cosmos", "Video Augmentation; Synthetic Data Generation"],
} as const;

const VIDEO_AUGMENTATION_PIPELINE_FEATURES = [
  [
    "RGB Video Ingestion",
    "Starts from real or simulated RGB video plus a user-defined configuration that specifies target scenarios, variations, labels, and downstream training or evaluation needs.",
  ],
  [
    "Auto-Labeling and Scene Understanding",
    "Uses detection, tracking, VLM captioning, event analysis, and MCQ generation to turn raw video into structured labels and augmentation instructions.",
  ],
  [
    "Cosmos-Powered Video Augmentation",
    "Uses NVIDIA Cosmos Transfer 2.5 and related Cosmos assets to synthesize controlled variations across weather, lighting, operating conditions, and scene context.",
  ],
  [
    "Evaluator-Checked Augmented Video Output",
    "Produces augmented video, annotations, captions, event metadata, MCQs, logs, and generated configs for model training, scenario coverage analysis, regression testing, and larger physical AI data pipelines.",
  ],
] as const;

const VIDEO_AUGMENTATION_AGENT_STEPS = [
  "Turns that request into a complete OSMO video augmentation and auto-labeling workflow.",
  "Prepares input video from your dataset URL, local upload, or the built-in demo assets.",
  "Selects the right workflow mode: augmentation and auto-labeling, auto-labeling only, parallel end to end, or super-resolution gated end to end.",
  "Runs auto-labeling and scene understanding with detection, tracking, captions, event metadata, prompt generation, and MCQ outputs.",
  "Uses NVIDIA Cosmos Transfer 2.5 and Cosmos model assets to create controlled video variants across weather, lighting, road or floor condition, and scene context.",
  "Runs guardrails, captioning, hallucination checks, and output inspection so generated videos and labels are ready for review.",
  "Exports augmented videos, labels, metadata, logs, generated configs, and optional downloaded artifacts for downstream model training and evaluation.",
] as const;

export const VideoAugmentationHeader: React.FC = () => (
  <div className="flex flex-col">
    <h1 className="text-white text-3xl font-semibold">
      {VIDEO_AUGMENTATION_HEADER.title}
    </h1>
    <p className="text-secondary text-sm mt-2">
      {VIDEO_AUGMENTATION_HEADER.summary}
    </p>
    <div className="flex items-center gap-2 flex-wrap mt-4">
      {VIDEO_AUGMENTATION_HEADER.badges.map((badge) => (
        <span
          key={badge}
          className="text-xs text-white/70 border border-zinc-700 px-2.5 py-1 rounded-full"
        >
          {badge}
        </span>
      ))}
    </div>
  </div>
);

const VideoAugmentationOverview: React.FC = () => (
  <div className="flex flex-col gap-5">
    <div>
      <Paragraph>
        Turn real or simulated RGB video into diverse, model-ready physical AI
        datasets using agent skills to curate, augment, auto-label, and generate
        training data in one unified workflow. Powered by NVIDIA Cosmos and
        NVIDIA OSMO, the launchable helps teams expand scenario coverage, reduce
        costly real-world collection, and avoid stitching together fragmented
        data pipelines by hand.
      </Paragraph>
    </div>

    <div className="aspect-video overflow-hidden rounded-md border border-zinc-800 bg-black">
      <iframe
        className="h-full w-full"
        src="https://www.youtube.com/embed/KZV5sn1duaU"
        title="How to Augment Videos at Scale With Open-Source NVIDIA Physical AI Agent Skills"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
      />
    </div>

    <div>
      <SectionHeading>Agent Experience</SectionHeading>
      <Paragraph>
        Open the launchable on Brev, click the Claw link from the Instance
        Access page, and type a simple goal such as:
      </Paragraph>
      <div className="flex flex-col gap-3 mt-3">
        <PromptExample>“Demo video augmentation for me.”</PromptExample>
        <PromptExample>
          “label this source video without augmentation,”
        </PromptExample>
        <PromptExample>
          “Demo for video augmentation using this{" "}
          <InlineLink href="https://huggingface.co/datasets/nvidia/video-data-augmentation-demo/blob/main/03_IllegalOccupation_020_10FPS.mp4">
            video
          </InlineLink>
          .”
        </PromptExample>
      </div>
      <div className="mt-3">
        <Paragraph>
          The claw tool invocation and thinking verbosity view can be toggled on
          / off from the claw UI per user preference.
        </Paragraph>
      </div>
      <p className="text-sm text-white/70 leading-relaxed mt-3">
        <span className="font-semibold text-white/90">
          Time to first output:
        </span>{" "}
        about 50 minutes to first augmented video output, depending on setup,
        input size, and workflow mode
      </p>
      <p className="text-sm font-semibold text-white/90 mt-3">The Agent:</p>
      <ul className="list-disc list-inside text-sm text-white/85 flex flex-col gap-2 mt-2">
        {VIDEO_AUGMENTATION_AGENT_STEPS.map((step) => (
          <li key={step}>{step}</li>
        ))}
      </ul>
    </div>

    <div>
      <SectionHeading>Pipeline Features</SectionHeading>
      <div className="grid grid-cols-1 gap-2">
        {VIDEO_AUGMENTATION_PIPELINE_FEATURES.map(([feature, detail], i) => (
          <div
            key={feature}
            className="flex gap-3 items-start rounded-md border border-zinc-800 bg-zinc-900/40 px-4 py-3 transition-colors"
          >
            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-zinc-700 text-[10px] font-bold text-white/60">
              {i + 1}
            </span>
            <div className="flex flex-col gap-0.5">
              <span className="text-sm font-semibold text-white/90">
                {feature}
              </span>
              <span className="text-xs text-white/55 leading-relaxed">
                {detail}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const VideoAugmentationDetails: React.FC = () => (
  <div className="flex flex-col gap-5">
    <div>
      <Paragraph>
        Physical AI needs vast, diverse, precisely labeled data that captures
        the long tail of the real world. Collecting tens of thousands of
        real-world examples is expensive and slow, while fragmented
        synthetic-data pipelines limit scale. Video Data Augmentation brings
        data staging, augmentation, auto-labeling, evaluation, and agentic
        orchestration into a single OSMO-backed workflow powered by NVIDIA
        Cosmos. It transforms real and simulated video inputs into
        high-fidelity, model-ready datasets so teams can accelerate iteration,
        improve scenario coverage, and scale physical AI training and
        evaluation.
      </Paragraph>
    </div>

    <div>
      <SectionHeading>What It Does</SectionHeading>
      <Paragraph>
        The pipeline ingests RGB video and a target augmentation specification.
        It can run auto-labeling on original videos, generate augmented
        variants, auto-label augmented outputs, or run end-to-end workflows that
        combine both. Depending on the selected mode, it uses detection,
        tracking, VLM captioning, event analysis, MCQ generation, optional
        super-resolution, Cosmos Transfer 2.5 augmentation, Cosmos Guardrail,
        hallucination checks, and output verification. OSMO handles workflow
        submission, data movement, GPU resource use, logs, and scalable
        execution.
      </Paragraph>
      <Paragraph>
        The output is an augmented video dataset with generated configs,
        augmented clips, detections, tracks, captions, event metadata, MCQs,
        evaluation artifacts, workflow logs, and downloadable results for
        downstream training, simulation, regression testing, and model
        evaluation.
      </Paragraph>
    </div>

    <div>
      <SectionHeading>Getting Started</SectionHeading>
      <p className="text-sm font-semibold text-white/90 mb-2">
        1. Prerequisites
      </p>
      <Paragraph>
        Before running any workflows, open Claw or your preferred coding agent
        in the deployed environment and gather the following:
      </Paragraph>
      <ul className="list-disc list-inside text-sm text-white/85 flex flex-col gap-2 mt-2">
        <li>
          <span className="font-semibold text-white/90">
            NVIDIA Build API Key (or Agent API Key):
          </span>{" "}
          To power agent harness for endpoint-backed model calls.
        </li>
        <li>
          <span className="font-semibold text-white/90">
            Hugging Face Token:
          </span>{" "}
          For model and dataset downloads (
          <InlineLink href="https://github.com/NVIDIA/physical-ai-data-factory/blob/main/docs/workflows/physical-ai-video-data-augmentation/launchable.md#before-you-run">
            you must accept the licenses on the individual repos first
          </InlineLink>
          ).
        </li>
        <li>
          <span className="font-semibold text-white/90">
            Source Video or Dataset URL:
          </span>{" "}
          You can provide your own data asset link, or omit it to let the agent
          stage built-in demo assets.{" "}
          <InlineLink href="https://github.com/NVIDIA/physical-ai-data-factory/blob/main/docs/workflows/physical-ai-video-data-augmentation/launchable.md#input-options">
            See input options
          </InlineLink>
          .
        </li>
      </ul>

      <p className="text-sm font-semibold text-white/90 mt-4 mb-2">
        2. Initial Generation
      </p>
      <Paragraph>
        Once your credentials are ready, you can start the workflow by
        interacting with your agent. For example, paste a prompt like this into
        OpenClaw or your coding agent&apos;s chat:
      </Paragraph>
      <div className="mt-3">
        <PromptExample>
          “I want a demo for video augmentation using this video
          https://huggingface.co/datasets/nvidia/video-data-augmentation-demo/blob/main/03_IllegalOccupation_020_10FPS.mp4.
          My Hugging Face token is{" "}
          <code className="text-white/85 bg-zinc-800 px-1 rounded">
            &lt;your-hf-token&gt;
          </code>
          ”
        </PromptExample>
      </div>

      <p className="text-sm font-semibold text-white/90 mt-4 mb-2">
        3. Uploading and Downloading From the Instance
      </p>
      <Paragraph>
        You can use standard{" "}
        <code className="text-white/85 bg-zinc-800 px-1 rounded">scp</code>{" "}
        (Secure Copy Protocol) to upload custom datasets or download generated
        results directly to and from your instance via the terminal.
      </Paragraph>
      <p className="text-sm font-semibold text-white/90 mt-3 mb-2">
        Uploading to the instance:
      </p>
      <ul className="list-disc list-inside text-sm text-white/85 flex flex-col gap-2">
        <li>
          <span className="font-semibold text-white/90">Single File:</span>{" "}
          <code className="text-white/85 bg-zinc-800 px-1 rounded">
            scp /path/to/local/video.mp4 ubuntu@instance_ip:/remote/directory/
          </code>
        </li>
        <li>
          <span className="font-semibold text-white/90">Entire Folder:</span>{" "}
          <code className="text-white/85 bg-zinc-800 px-1 rounded">
            scp -r /path/to/local/folder ubuntu@instance_ip:/remote/directory/
          </code>
        </li>
      </ul>
      <p className="text-sm font-semibold text-white/90 mt-3 mb-2">
        Downloading from the instance:
      </p>
      <ul className="list-disc list-inside text-sm text-white/85 flex flex-col gap-2">
        <li>
          <span className="font-semibold text-white/90">Single File:</span>{" "}
          <code className="text-white/85 bg-zinc-800 px-1 rounded">
            scp ubuntu@instance_ip:/remote/directory/output.mp4
            /path/to/local/directory/
          </code>
        </li>
        <li>
          <span className="font-semibold text-white/90">Entire Folder:</span>{" "}
          <code className="text-white/85 bg-zinc-800 px-1 rounded">
            scp -r ubuntu@instance_ip:/remote/directory/results
            /path/to/local/directory/
          </code>
        </li>
      </ul>
      <Paragraph>If you wish to perserve logs from you session:</Paragraph>
      <ul className="list-disc list-inside text-sm text-white/85 flex flex-col gap-2 mt-2">
        <li>
          <span className="font-semibold text-white/90">Claw Logs:</span> Use{" "}
          <code className="text-white/85 bg-zinc-800 px-1 rounded">
            /export-session
          </code>{" "}
          to download the current session logs
        </li>
        <li>
          <span className="font-semibold text-white/90">OSMO Logs:</span> Logs
          can be downloaded directly from the OSMO UI under Workflows &gt;
          YOUR_WORKFLOW &gt; Logs
        </li>
      </ul>
    </div>

    <hr className="border-zinc-800" />

    <div>
      <SectionHeading>Supported Workflows</SectionHeading>
      <Paragraph>
        The pipeline ingests RGB video and a target augmentation specification.
        It handles workflow submission, data movement, GPU resource use, logs,
        and scalable execution through OSMO.
      </Paragraph>
      <div className="overflow-x-auto mt-3">
        <table className="w-full text-sm text-white/85 border border-zinc-800 rounded-md">
          <thead>
            <tr className="border-b border-zinc-800 text-left">
              <th className="px-3 py-2 font-medium">Mode</th>
              <th className="px-3 py-2 font-medium">What Runs</th>
              <th className="px-3 py-2 font-medium">Typical Use Case</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-zinc-800">
              <td className="px-3 py-2">
                <code className="text-white/85 bg-zinc-800 px-1 rounded">
                  augmentation_and_al
                </code>
              </td>
              <td className="px-3 py-2">
                Augmentation → auto-labeling on augmented videos
              </td>
              <td className="px-3 py-2">
                Default for &quot;augment and label&quot; requests
              </td>
            </tr>
            <tr className="border-b border-zinc-800">
              <td className="px-3 py-2">
                <code className="text-white/85 bg-zinc-800 px-1 rounded">
                  auto_labeling
                </code>
              </td>
              <td className="px-3 py-2">
                Auto-labeling on original videos only
              </td>
              <td className="px-3 py-2">
                Label, caption, and event extraction without running
                augmentation
              </td>
            </tr>
            <tr className="border-b border-zinc-800">
              <td className="px-3 py-2">
                <code className="text-white/85 bg-zinc-800 px-1 rounded">
                  e2e
                </code>
              </td>
              <td className="px-3 py-2">
                Auto-label originals and run augmentation in parallel →
                auto-label augmented
              </td>
              <td className="px-3 py-2">
                Explicit fast or throughput-first full-pipeline runs
              </td>
            </tr>
            <tr>
              <td className="px-3 py-2">
                <code className="text-white/85 bg-zinc-800 px-1 rounded">
                  e2e_super_resolution
                </code>
              </td>
              <td className="px-3 py-2">
                Super-resolution + auto-label originals (sequentially gated) →
                augmentation → auto-label augmented
              </td>
              <td className="px-3 py-2">
                Best when low-resolution inputs need a quality gate before
                augmentation
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <hr className="border-zinc-800" />

    <div>
      <SectionHeading>Running the Workflows</SectionHeading>
      <Paragraph>
        Depending on the selected mode, the pipeline leverages detection,
        tracking, VLM captioning, event analysis, MCQ generation, optional
        super-resolution, Cosmos Transfer 2.5 augmentation, Cosmos Guardrail,
        hallucination checks, and output verification.
      </Paragraph>
      <p className="text-sm font-semibold text-white/90 mt-3 mb-2">
        Example Prompts:
      </p>
      <ul className="list-disc list-inside text-sm text-white/85 flex flex-col gap-2">
        <li>
          <span className="italic">Demo Run:</span> “Demo video augmentation for
          me”
        </li>
        <li>
          <span className="italic">Custom Target Conditions:</span> “Augment my
          video{" "}
          <code className="text-white/85 bg-zinc-800 px-1 rounded">
            &lt;s3-or-public-url&gt;
          </code>{" "}
          to create diversity in my dataset. Also show MCQs”
        </li>
        <li>
          <span className="italic">Targeted Variation:</span> “Run augmentation
          and captions on the video at{" "}
          <code className="text-white/85 bg-zinc-800 px-1 rounded">
            &lt;path&gt;
          </code>
          . Augment the video to have{" "}
          <span className="font-semibold text-white/90">sunny conditions</span>
          .”
        </li>
        <li>
          <span className="italic">Lighting Changes:</span> “I have this video{" "}
          <code className="text-white/85 bg-zinc-800 px-1 rounded">
            &lt;path&gt;
          </code>
          , i want to create augmented videos with the{" "}
          <span className="font-semibold text-white/90">dim lighting</span>.”
        </li>
      </ul>

      <p className="text-sm font-semibold text-white/90 mt-4">
        Typical Outputs Include:
      </p>
      <ul className="list-disc list-inside text-sm text-white/85 flex flex-col gap-2 mt-2">
        <li>Augmentation config YAML files</li>
        <li>
          Auto-label outputs (detections, tracks, captions, event metadata, and
          MCQ JSON) for original and/or augmented videos
        </li>
        <li>
          Augmented videos with controlled weather, lighting, and scene
          variations via Cosmos Transfer 2.5
        </li>
        <li>Per-run logs, generated configs, and evaluation artifacts</li>
      </ul>
    </div>

    <hr className="border-zinc-800" />

    <div>
      <SectionHeading>Augmentation Capabilities</SectionHeading>
      <Paragraph>
        The VDA pipeline is designed to modify{" "}
        <span className="font-semibold text-white/90">
          scene-level changes for visual diversity
        </span>
        , rather than structural or targeted video editing.
      </Paragraph>
      <ul className="list-disc list-inside text-sm text-white/85 flex flex-col gap-2 mt-2">
        <li>
          <span className="font-semibold text-white/90">
            What it does (Global &amp; Scene-Level):
          </span>{" "}
          The workflow excels at altering broad environmental variables. You can
          reliably transform the weather (rain, snow, fog), lighting (dim,
          bright, harsh shadows), time of day, and surface conditions (wet
          roads, dusty warehouse floors).
        </li>
        <li>
          <span className="font-semibold text-white/90">
            What it does NOT do (Specific &amp; Object-Level):
          </span>{" "}
          The pipeline is not designed to create or modify specific, localized
          elements. You cannot use it to add new entities to a scene (e.g.,
          spawning a new pedestrian), modify a specific target (e.g., turning a
          blue sedan into a red truck), or alter object trajectories and
          physics.
        </li>
      </ul>
    </div>

    <hr className="border-zinc-800" />

    <div>
      <SectionHeading>
        Best Practices: Getting the Most Out of the Agent
      </SectionHeading>
      <ul className="list-disc list-inside text-sm text-white/85 flex flex-col gap-2">
        <li>
          <span className="font-semibold text-white/90">
            Be explicit about the use case:
          </span>{" "}
          Name your target conditions (e.g., weather, lighting, time of day,
          floor/road condition) and your selected workflow mode up front.
        </li>
        <li>
          <span className="font-semibold text-white/90">
            State your bounds:
          </span>{" "}
          Clear details regarding augmentation counts, parallel workers, or
          specific video asset paths keep the agent from choosing defaults you
          might not want.
        </li>
        <li>
          <span className="font-semibold text-white/90">
            Hand it credentials inline:
          </span>{" "}
          Pasting your Hugging Face token or asset paths directly into the
          prompt skips the conversational back-and-forth and goes straight to
          deployment.
        </li>
        <li>
          <span className="font-semibold text-white/90">
            Let long-running jobs run as subagents:
          </span>{" "}
          Submission, monitoring, and download tasks run smoothly in the
          background so your main chat stays responsive.
        </li>
        <li>
          <span className="font-semibold text-white/90">
            Scale out progressively:
          </span>{" "}
          If testing on your own infrastructure, start with small tests before
          increasing dataset size, augmentation counts, and parallel workers in
          increments.
        </li>
      </ul>
    </div>

    <hr className="border-zinc-800" />

    <div>
      <SectionHeading>Frequently Asked Questions</SectionHeading>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <p className="text-sm font-semibold text-white/90">
            Q: Can I get a more diverse training video with just one clip?
          </p>
          <Paragraph>
            A: Yes. You can start with one video input, such as driving footage,
            robot ego-centric video, a retail camera clip, warehouse video, or
            surveillance footage, and generate controlled variations across
            weather, lighting, motion, scene context, and operating conditions.
          </Paragraph>
        </div>
        <div className="flex flex-col gap-1">
          <p className="text-sm font-semibold text-white/90">
            Q: Do I need my own video to try this?
          </p>
          <Paragraph>
            A: No. The launchable supports a default demo path using VDA demo
            assets, so you can run a quick validation before bringing your own
            data.
          </Paragraph>
        </div>
        <div className="flex flex-col gap-1">
          <p className="text-sm font-semibold text-white/90">
            Q: Which teams and domains is this built for?
          </p>
          <Paragraph>
            A: It is built for autonomous vehicle, robotics, AMR, smart-space,
            and Vision AI teams that need more diverse labeled video data for
            training, evaluation, and regression testing.
          </Paragraph>
        </div>
        <div className="flex flex-col gap-1">
          <p className="text-sm font-semibold text-white/90">
            Q: What variations can the agent generate?
          </p>
          <Paragraph>
            A: Examples include weather, lighting, time of day, road or floor
            condition, scene context, and long-tail operating conditions. You
            can steer the workflow with prompts such as rainy conditions, dim
            lighting, wet floors, or multiple variants for the same input.
          </Paragraph>
        </div>
        <div className="flex flex-col gap-1">
          <p className="text-sm font-semibold text-white/90">
            Q: Can the agent just label my video without augmenting it?
          </p>
          <Paragraph>
            A: Yes. The auto_labeling workflow labels original videos only,
            producing detections, tracks, captions, event metadata, and MCQs
            without running augmentation.
          </Paragraph>
        </div>
        <div className="flex flex-col gap-1">
          <p className="text-sm font-semibold text-white/90">
            Q: How do I tune augmentation intensity or rerun with different
            parameters?
          </p>
          <Paragraph>
            A: Prompt the agent with the new target conditions, number of
            variants, workflow mode, or scaling goal. The workflow can adjust
            augmentation count, lighting, weather, floor or road conditions, and
            parallel workers for a new run.
          </Paragraph>
        </div>
        <div className="flex flex-col gap-1">
          <p className="text-sm font-semibold text-white/90">
            Q: Can I run on my own infrastructure instead of the Brev
            launchable?
          </p>
          <Paragraph>
            A: Yes. You can run scaled-out tests on your own infrastructure
            using the physical-ai-video-data-augmentation skills. Match the
            launchable resource guidance, point runs to your own pool and object
            storage paths, and increase dataset size, augmentation count, and
            parallel workers gradually.
          </Paragraph>
        </div>
        <div className="flex flex-col gap-1">
          <p className="text-sm font-semibold text-white/90">
            Q: How do I get started?
          </p>
          <Paragraph>
            A: Deploy the Brev launchable, open Claw from Secure Links, complete
            setup with your NVIDIA Build API Key and Hugging Face credentials,
            then start from one of the example prompts.
          </Paragraph>
        </div>
      </div>
    </div>
  </div>
);

export const VideoAugmentationSkin: React.FC<LaunchableContentTabProps> = (
  props,
) => (
  <PhysicalAISkin
    {...props}
    skillFolderUrl={VIDEO_AUGMENTATION_SKILL_FOLDER_URL}
    overview={<VideoAugmentationOverview />}
    details={<VideoAugmentationDetails />}
  />
);
